import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { Alert, Button, Card, CardHeader, ConfirmPrompt, Field, Input, Mono, Select } from "../../components/ui";
import { Icon, ProviderMark } from "../../components/icons";
import { Modal } from "../../components/Modal";
import { ApiError } from "../../lib/api";
import {
  useApp,
  useAppLifecycle,
  useDeleteApp,
  useGitBranches,
  useGitConnections,
  usePlans,
  useUpdateApp,
} from "../../lib/queries";
import { PlanPicker, formatPrice, planSpec } from "../../components/PlanPicker";
import type { BuildType } from "../../lib/types";
import { BUILD_TYPES } from "../../lib/buildTypes";
import { useAppTabContext } from "./context";

export function SettingsTab() {
  const { t } = useTranslation("app");
  const { orgId, appId } = useAppTabContext();
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const canEdit = useCan("developer");
  const canDelete = useCan("admin");

  const { data: app } = useApp(orgId, appId);
  const plans = usePlans(orgId);
  const connections = useGitConnections(orgId);
  const source = app?.source;
  const sourceConnection =
    source?.type === "repo"
      ? connections.data?.find((c) => c.id === source.connectionId)
      : undefined;
  const updateApp = useUpdateApp(orgId ?? "", appId ?? "");
  const deleteApp = useDeleteApp(orgId ?? "");
  const lifecycle = useAppLifecycle(orgId ?? "", appId ?? "");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [buildType, setBuildType] = useState<BuildType>("railpack");
  const [port, setPort] = useState("");
  const [autoDeploy, setAutoDeploy] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [planNotice, setPlanNotice] = useState<string | null>(null);
  const [changingBranch, setChangingBranch] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState("");
  /** The branch just switched to, for the moment before the app refetches. */
  const [switchedTo, setSwitchedTo] = useState<string | null>(null);
  const [branchError, setBranchError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!app) return;
    setName(app.name);
    setDescription(app.description ?? "");
    setBuildType(app.buildType);
    setPort(app.port ? String(app.port) : "");
    if (app.source.type === "repo") setAutoDeploy(app.source.autoDeploy);
    setSelectedPlanId(app.planId);
  }, [app]);

  // Asked for only once somebody opens the picker: it is a call to the git
  // host, and most visits to this tab never change the branch.
  const repo =
    source?.type === "repo" && source.connectionId && source.owner && source.repository
      ? { connectionId: source.connectionId, owner: source.owner, repository: source.repository, branch: source.branch }
      : null;
  const branches = useGitBranches(
    orgId,
    changingBranch && repo ? repo.connectionId : null,
    changingBranch && repo ? { owner: repo.owner, repository: repo.repository } : null,
  );
  // The host lists the first hundred; the current branch stays selectable
  // even when it is not among them, and before the list arrives.
  const branchOptions = (() => {
    const listed = branches.data ?? [];
    const current = repo?.branch;
    return current && !listed.some((b) => b.name === current)
      ? [{ name: current, isDefault: false }, ...listed]
      : listed;
  })();

  function startChangingBranch() {
    setBranchError(null);
    setSelectedBranch(repo?.branch ?? "");
    setChangingBranch(true);
  }

  /**
   * Saves the branch and builds nothing: what runs now keeps running until a
   * deploy, a push to the new branch, or a redeploy -- each of which clones
   * the new branch. The notice offers the deploy rather than doing it, so a
   * stopped app stays stopped and a wrong pick is not already live.
   */
  async function saveBranch() {
    setBranchError(null);
    try {
      await updateApp.mutateAsync({ branch: selectedBranch });
      setChangingBranch(false);
      setSwitchedTo(selectedBranch);
    } catch (cause) {
      setBranchError(cause instanceof ApiError ? cause.message : t("settings.branchFailed"));
    }
  }

  // From the app's own record, so a reload still says the branch is not live
  // yet and still offers to deploy it.
  const pendingBranch = app?.branchDeployPending && repo?.branch ? repo.branch : switchedTo;

  async function deployNow() {
    if (!pendingBranch) return;
    setBranchError(null);
    try {
      await lifecycle.mutateAsync({
        action: "deploy",
        title: t("settings.deployTitle", { branch: pendingBranch }),
      });
      setSwitchedTo(null);
    } catch (cause) {
      setBranchError(cause instanceof ApiError ? cause.message : t("settings.deployFailed"));
    }
  }

  /**
   * Dokploy applies CPU and memory when the container is next recreated, so the
   * new limits are not live yet. The API says so in `pendingRedeploy` and it is
   * repeated here rather than letting someone assume their app just got bigger.
   */
  async function savePlan() {
    if (!selectedPlanId) return;
    setError(null);
    setPlanNotice(null);
    try {
      const result = await updateApp.mutateAsync({ planId: selectedPlanId });
      setChangingPlan(false);
      setPlanNotice(
        (result as { pendingRedeploy?: string | null }).pendingRedeploy ??
          t("settings.planUpdated"),
      );
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("settings.planFailed"));
    }
  }

  /**
   * Saved on toggle rather than behind the "Save changes" button below: this
   * one switch changes what happens on the customer's next push, and leaving it
   * looking flipped while it is not yet saved would be a lie about production.
   */
  async function toggleAutoDeploy(next: boolean) {
    setError(null);
    setAutoDeploy(next);
    try {
      await updateApp.mutateAsync({ autoDeploy: next });
    } catch (cause) {
      setAutoDeploy(!next);
      setError(cause instanceof ApiError ? cause.message : t("settings.pushFailed"));
    }
  }

  async function save() {
    setError(null);
    setSaved(false);
    const parsedPort = Number.parseInt(port, 10);
    try {
      await updateApp.mutateAsync({
        name: name.trim(),
        description: description.trim() === "" ? null : description.trim(),
        buildType,
        port: Number.isFinite(parsedPort) ? parsedPort : null,
      });
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("settings.saveFailed"));
    }
  }

  async function confirmDelete() {
    if (!appId) return;
    try {
      await deleteApp.mutateAsync(appId);
      void navigate("/apps", { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("settings.deleteFailed"));
      setConfirmOpen(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title={t("settings.general")} />
        <div className="space-y-5 p-5">
          {error && <Alert>{error}</Alert>}
          {saved && <Alert tone="success">{t("settings.saved")}</Alert>}

          <Field label={t("settings.name")} htmlFor="appName">
            <Input
              id="appName"
              value={name}
              disabled={!canEdit}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label={t("settings.description")} htmlFor="appDescription">
            <Input
              id="appDescription"
              value={description}
              disabled={!canEdit}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("settings.buildType")}
              htmlFor="appBuildType"
                hint={t("settings.buildTypeHint")}
            >
              <Select
                id="appBuildType"
                value={buildType}
                disabled={!canEdit}
                onChange={(event) => setBuildType(event.target.value as BuildType)}
              >
                {BUILD_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {t(`build.${value}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={t("settings.port")} htmlFor="appPort">
              <Input
                id="appPort"
                type="number"
                min={1}
                max={65535}
                value={port}
                disabled={!canEdit}
                onChange={(event) => setPort(event.target.value)}
              />
            </Field>
          </div>

          {canEdit && (
            <Button onClick={() => void save()} loading={updateApp.isPending}>
              {t("settings.save")}
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t("settings.plan")}
          description={t("settings.planDescription")}
          action={
            canEdit && !changingPlan ? (
              <Button variant="secondary" onClick={() => setChangingPlan(true)}>
                {t("settings.changePlan")}
              </Button>
            ) : undefined
          }
        />
        <div className="space-y-4 p-5">
          {planNotice && <Alert tone="info">{planNotice}</Alert>}

          {changingPlan ? (
            <>
              <PlanPicker
                plans={plans.data ?? []}
                loading={plans.isLoading}
                value={selectedPlanId}
                onChange={setSelectedPlanId}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setChangingPlan(false);
                    setSelectedPlanId(app?.planId ?? null);
                  }}
                >
                  {t("settings.cancel")}
                </Button>
                <Button
                  loading={updateApp.isPending}
                  disabled={!selectedPlanId || selectedPlanId === app?.planId}
                  onClick={() => void savePlan()}
                >
                  {t("settings.movePlan")}
                </Button>
              </div>
            </>
          ) : app?.plan ? (
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-medium text-fg">{app.plan.name}</span>
              {/* Only when the name does not already state them — current tier
                  names are the specification, and repeating it beside itself
                  reads as a rendering bug. */}
              {app.plan.name !== planSpec(app.plan) && (
                <span className="text-sm text-fg-muted">{planSpec(app.plan)}</span>
              )}
              <span className="text-sm text-fg-subtle">
                {formatPrice(app.plan.price.cents, app.plan.price.currency)}/mo
              </span>
            </div>
          ) : (
            <p className="text-sm text-fg-subtle">
              {t("settings.noPlan")}
            </p>
          )}
        </div>
      </Card>

      {app?.source.type === "repo" && (
        <Card>
          <CardHeader
            title={t("settings.source")}
            description={t("settings.sourceDescription")}
            action={
              canEdit && repo && !changingBranch ? (
                <Button variant="secondary" onClick={startChangingBranch}>
                  {t("settings.changeBranch")}
                </Button>
              ) : undefined
            }
          />
          <div className="space-y-5 p-5">
            {branchError && <Alert>{branchError}</Alert>}
            {pendingBranch && (
              <Alert tone="info">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span>{t("settings.branchSwitched", { branch: pendingBranch })}</span>
                  {canEdit && (
                    <Button size="sm" loading={lifecycle.isPending} onClick={() => void deployNow()}>
                      {t("settings.deployNow")}
                    </Button>
                  )}
                </div>
              </Alert>
            )}

            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-fg-subtle">{t("settings.repository")}</dt>
                <dd className="mt-1 flex min-w-0 items-center gap-2 text-sm text-fg">
                  {/*
                    The provider's own mark, not a generic branch glyph. An
                    organization can connect GitHub and GitLab at once and the
                    same owner/name can exist on both, so the path alone does not
                    say where this builds from.
                  */}
                  {sourceConnection ? (
                    <ProviderMark
                      provider={sourceConnection.provider}
                      className="size-4 shrink-0 text-fg-subtle"
                    />
                  ) : (
                    <Icon name="branch" className="size-4 shrink-0 text-fg-subtle" />
                  )}
                  <Mono className="break-all">
                    {app.source.repoFullName ?? `${app.source.owner}/${app.source.repository}`}
                  </Mono>
                </dd>
                {sourceConnection && (
                  <p className="mt-1 text-xs text-fg-subtle">
                    {t("settings.via", { account: sourceConnection.account.login })}
                  </p>
                )}
              </div>
              <div>
                <dt className="text-xs font-medium text-fg-subtle">{t("settings.branch")}</dt>
                <dd className="mt-1 text-sm text-fg">
                  <Mono className="break-all">{app.source.branch}</Mono>
                </dd>
              </div>
              {app.source.deployKeyFingerprint && (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium text-fg-subtle">
                    {t("settings.fingerprint")}
                  </dt>
                  <dd className="mt-1 text-sm break-all text-fg">
                    {/* Lets the customer match this against the read-only key
                        listed in their own repository settings. */}
                    <Mono>{app.source.deployKeyFingerprint}</Mono>
                  </dd>
                </div>
              )}
            </dl>

            {changingBranch && (
              <>
                {branches.error && (
                  <Alert>
                    {branches.error instanceof ApiError
                      ? branches.error.message
                      : t("settings.branchesFailed")}
                  </Alert>
                )}
                <Field
                  label={t("settings.branch")}
                  htmlFor="appBranch"
                  hint={branches.isLoading ? t("settings.loadingBranches") : t("settings.branchKeeps")}
                >
                  <Select
                    id="appBranch"
                    value={selectedBranch}
                    disabled={branches.isLoading}
                    onChange={(event) => setSelectedBranch(event.target.value)}
                  >
                    {branchOptions.map((branch) => (
                      <option key={branch.name} value={branch.name}>
                        {branch.isDefault
                          ? t("settings.defaultBranch", { branch: branch.name })
                          : branch.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" onClick={() => setChangingBranch(false)}>
                    {t("settings.cancel")}
                  </Button>
                  <Button
                    loading={updateApp.isPending}
                    disabled={!selectedBranch || selectedBranch === repo?.branch}
                    onClick={() => void saveBranch()}
                  >
                    {t("settings.switchBranch")}
                  </Button>
                </div>
              </>
            )}

            {app.source.pushDeliveryActive ? (
              <label className="flex items-start gap-2.5 rounded-lg border border-line p-3 text-sm">
                <input
                  type="checkbox"
                  checked={autoDeploy}
                  disabled={!canEdit || updateApp.isPending}
                  onChange={(event) => void toggleAutoDeploy(event.target.checked)}
                  className="mt-0.5 size-4 accent-accent"
                />
                <span>
                  <span className="font-medium text-fg">
                    {t("settings.autoDeploy", { branch: app.source.branch })}
                  </span>
                  <span className="mt-0.5 block text-fg-subtle">
                    {t("settings.immediate")}
                  </span>
                </span>
              </label>
            ) : (
              <Alert tone="warning">
                {t("settings.noWebhook")}
              </Alert>
            )}
          </div>
        </Card>
      )}

      {canDelete && (
        <Card className="border-danger/40">
          <CardHeader
            title={t("settings.delete")}
            description={t("settings.deleteDescription")}
          />
          <div className="p-5">
            <Button variant="danger" onClick={() => setConfirmOpen(true)}>
              {t("settings.deleteAction")}
            </Button>
          </div>
        </Card>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t("settings.deleteTitle", { name: app?.name ?? t("settings.thisApp") })}
        description={t("settings.deleteModalDescription")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t("settings.cancel")}
            </Button>
            <Button
              variant="danger"
              disabled={confirmText !== app?.name}
              loading={deleteApp.isPending}
              onClick={() => void confirmDelete()}
            >
              {t("settings.deletePermanently")}
            </Button>
          </>
        }
      >
        <Field
          label={
            <ConfirmPrompt phrase={app?.name ?? ""} sentence={(slot) => t("settings.confirmLabel", { name: slot })} />
          }
          htmlFor="confirmName"
          hint={currentOrg ? t("settings.belongsTo", { organization: currentOrg.name }) : undefined}
        >
          <Input
            id="confirmName"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}
